import { Component, Element, h, Host, Prop } from '@stencil/core'

import { CssClassMap } from '../../utils/interfaces'

let MagicButtonId = 0

/**
 * Para uma descrição detalhada, consulte a [documentação do GovBR-DS](https://www.gov.br/ds/components/magicbutton?tab=designer).
 *
 * @slot default - Conteúdo interno do botão (texto ou ícone).
 */
@Component({
  tag: 'br-magic-button',
  styleUrl: 'magic-button.scss',
  shadow: true,
  formAssociated: true,
})
export class MagicButton {
  /**
   * Referência ao elemento host do componente.
   * Utilize esta propriedade para acessar e manipular o elemento DOM associado ao componente.
   */
  @Element() el: HTMLBrMagicButtonElement

  /**
   * Identificador único.
   * Se não for fornecido, será gerado automaticamente.
   */
  @Prop({ reflect: true }) readonly customId: string = `br-magic-button-${MagicButtonId++}`

  /**
   * Texto do rótulo do botão.
   * Utilizado como conteúdo principal quando presente.
   */
  @Prop({ reflect: true }) readonly label: string = ''

  /**
   * Nome do ícone a ser exibido.
   * Deve corresponder a um ícone disponível no sistema de ícones.
   */
  @Prop({ reflect: true }) readonly icon: string = ''

  /**
   * Densidade do botão.
   * Define o tamanho do componente:
   * - `large` para grande
   * - `medium` para médio
   * - `small` para pequeno
   */
  @Prop({ reflect: true }) readonly density: 'large' | 'medium' | 'small' = 'medium'

  /**
   * Define se o botão terá formato circular.
   * Aplica contorno arredondado completo quando `true` e não houver `label`.
   */
  @Prop({ reflect: true }) readonly circle: boolean = false

  /**
   * Texto de acessibilidade (ARIA label).
   * Deve ser fornecido para ícones sem texto para garantir acessibilidade.
   */
  @Prop() readonly ariaLabel: string = ''

  /**
   * Gera o mapeamento de classes CSS baseado nas propriedades de densidade.
   * @returns Mapa de classes CSS para o container do botão mágico.
   */
  private getCssClassMap(): CssClassMap {
    return {
      'br-magic-button': true,
      large: this.density === 'large',
      medium: this.density === 'medium',
      small: this.density === 'small',
    }
  }

  /**
   * Gera o mapeamento de classes CSS para aplicar o estilo de botão circular.
   * @returns Mapa contendo a classe `circle` quando aplicável.
   */
  private getCircleClass(): CssClassMap {
    return {
      circle: this.circle && !this.label,
    }
  }

  /**
   * Retorna a classe de CSS para posicionar ícone e label juntos.
   * @returns `label-icon` quando houver `label` e `icon`; string vazia caso contrário.
   */
  private getLabelIconClass(): string {
    return this.label && this.icon ? 'label-icon' : ''
  }

  /**
   * Renderiza o componente.
   */
  render() {
    const effectiveAriaLabel = this.ariaLabel || this.label || (this.icon ? `Botão com ícone ${this.icon}` : 'Botão')
    return (
      <Host id={this.customId}>
        <div class={this.getCssClassMap()}>
          <button class={{ 'br-button': true, ...this.getCircleClass() }} type="button" aria-label={effectiveAriaLabel}>
            {this.icon && <icon-base icon-name={this.icon} class={this.getLabelIconClass()} />}
            {this.label || <slot></slot>}
          </button>
        </div>
      </Host>
    )
  }
}
